/* -------------------------------------------------------------------------- */
/*                          SEMANTIC JSON DIFF PIPELINE                       */
/* -------------------------------------------------------------------------- */

/**
 * High-level overview:
 *
 * This pipeline converts two JSON values into a semantic, line-accurate diff
 * suitable for visualization inside the Monaco diff editor. JSON itself does
 * not preserve key order or formatting, but Monaco’s diff engine is entirely
 * line-based. Therefore, we must produce a stable, deterministic textual form
 * and map semantic deltas onto their visual line locations.
 *
 * Pipeline steps:
 *
 * 1. Deep-sort both JSON inputs by object key.
 *    - Sorting is performed recursively using json-deep-sort.
 *    - Arrays preserve their numeric index order (no move semantics).
 *    - Deterministic key order guarantees that structurally identical JSON
 *      produces identical printed line layouts, which is essential for
 *      reliable delta → line-number mapping.
 *
 * 2. Pretty-print both sorted JSON values using a custom deterministic printer.
 *    - Always 2-space indentation.
 *    - All objects and arrays are printed in multi-line form.
 *    - Empty containers are rendered as:
 *          {
 *          }
 *          [
 *          ]
 *      ensuring every container has an interior line where insertions can be
 *      virtually placed.
 *
 * 3. Reparse the printed JSON using json-to-ast with `loc: true`.
 *    - Produces a full AST with accurate `{ start: {line}, end: {line} }` ranges.
 *    - Walking the AST yields:
 *          canonicalPath → { startLine, endLine }
 *      where canonical paths are constructed using jsonpath-plus, e.g.:
 *          $.foo.bar[3]
 *
 * 4. Build the canonical-path → semantic delta map using microdiff.
 *    - Each delta includes a `path: (string | number)[]` describing the changed
 *      location in segment form (e.g., ["foo", 3, "bar"]).
 *    - We convert these segment arrays into canonical JSONPath strings
 *      (e.g., "$.foo[3].bar") using jsonpath-plus.
 *    - These canonical JSONPath strings will become the keys in our
 *      canonicalPath → semanticDelta map, where semanticDelta is the original
 * 			microdiff delta object.
 *
 * 5. Now, build the canonical-path → (semantic-delta, virtual/real line range) map.
 *    - For every canonical JSONPath string (e.g., "$.foo.bar[3]") that keys a
 *      semantic delta, determine its corresponding line range in the printed JSON.
 *
 *    Real range:
 *      - If this canonical path exists in the AST-generated map, return the
 *        actual start/end lines for that node.
 *
 *    Virtual range (when the path does not exist on that side):
 *      - Compute where the path *would* appear in the sorted JSON:
 *          • If the parent container is empty → insert at parent.startLine + 1.
 *          • For objects → insert before the first lexicographically later key,
 *            using the exact same sort logic used by json-deep-sort and the printer.
 *          • For arrays → insert after the previous sibling index.
 *          • If there is no earlier sibling → fall back to parent.endLine.
 *
 *    These rules ensure virtual computed lines exactly match the deterministic
 *    layout that the pretty printer would generate.
 *
 * 6. Convert semantic deltas + line ranges into an IDocumentDiff.
 *    - Produces Monaco-compatible DetailedLineRangeMapping objects describing
 *      CREATE, REMOVE, and CHANGE blocks.
 *    - Results are sorted by original line position to satisfy Monaco’s
 *      monotonicity requirements.
 *
 * Result:
 *    A deterministic, visually accurate, and semantically meaningful diff:
 *    every CREATE / REMOVE / CHANGE is mapped to the exact line it would occupy
 *    in the sorted, pretty-printed JSON—whether or not the key exists in either
 *    original or modified input.
 *
 * Additional:
 *    - Results are cached by JSON.stringify({sortedOriginal, sortedModified})
 *      to avoid recomputation when comparing identical structures.
 */

import parseJsonAst, {
	ASTNode,
	ObjectNode,
	ArrayNode,
} from "json-to-ast";

import type { JsonArray, JsonObject, JsonValue } from "type-fest";
import { JSONPath } from 'jsonpath-plus';
import { sort as deepSort } from '@tamtamchik/json-deep-sort';

import { DetailedLineRangeMapping, RangeMapping } from "vscode-diff";
import { LineRange } from "monaco-editor/esm/vs/editor/common/core/ranges/lineRange";
import { Range } from "monaco-editor/esm/vs/editor/common/core/range";
import diff, { Difference } from "microdiff";
import diff_match_patch, { diff_match_patch as DiffMatchPatch } from "diff-match-patch";

/* -------------------------------------------------------------------------- */
/*                               OUTPUT TYPES                                 */
/* -------------------------------------------------------------------------- */

// A JSON path in canonical form, e.g., $.foo.bar[3].
// Do not manually construct/deconstruct JSON paths - use the jsonpath-plus library instead. You don't want to be manually handling edge cases like escaping special chars and converting array indices.
//
// So, for example, to build a path, use:
// 		const path = JSONPath.toPathString(['$', 'foo', 'bar', 3]);  // This will give you the string path that you can use as a CanonicalPath
export type CanonicalPathString = `$${string}`;

// And to parse a path into segments, use:
// 		const segments = JSONPath.toPathArray('$.foo.bar[3]'); // These are the segments that make up the path. You can use this find parents, siblings, etc.
export type CanonicalPathSegments = ['$', ...string[]];

export interface PrintedRange {
	startLine: number;
	endLine: number;
}

export type PathToPrintedRangeMap = Map<CanonicalPathString, PrintedRange>;

export interface PathToPrintedRangeMapContainer {
	rangesByPath: PathToPrintedRangeMap;
	rootStart: number;
	rootEnd: number;
}

export type PathToSemanticDeltaMap = Map<CanonicalPathString, Difference>;

export interface SemanticDeltaAndPrintedRange {
	semanticDelta: Difference;
	originalRange: PrintedRange; // { startLine, endLine }
	modifiedRange: PrintedRange; // { startLine, endLine }
}

export type PathToSemanticDeltaAndPrintedRangeMap = Map<CanonicalPathString, SemanticDeltaAndPrintedRange>;

export interface SemanticDiffResults {
	sortedOriginalJsonPrinted: string;
	sortedModifiedJsonPrinted: string;
	deltaAndRangesByPath: PathToSemanticDeltaAndPrintedRangeMap;
}

export interface IDocumentDiff {
	readonly identical: boolean;
	readonly quitEarly: boolean;
	readonly changes: readonly DetailedLineRangeMapping[];
	readonly moves: readonly any[];
}

/* -------------------------------------------------------------------------- */
/*                                UTILITIES                                   */
/* -------------------------------------------------------------------------- */

function jsonPathArrayToString(segments: CanonicalPathSegments): CanonicalPathString {
	const pathString = JSONPath.toPathString(segments);
	if (pathString[0] !== '$') {
		throw new Error(`Expected JSON path to start with '$', got: ${pathString}`);
	}
	return `${pathString[0]}${pathString.slice(1)}`;
}

function jsonPathArrayToParent(pathSegments: CanonicalPathSegments): CanonicalPathSegments | undefined {
	const nonRootPathSegments = pathSegments.slice(1); // Remove the '$' at the start
	if (nonRootPathSegments.length === 0) {
		return undefined; // No parent for root path
	}
	nonRootPathSegments.pop(); // Remove the last segment
	return ['$', ...nonRootPathSegments];
}	

function jsonPathStringToArray(path: CanonicalPathString): CanonicalPathSegments {
	const segments = JSONPath.toPathArray(path);
	if (segments[0] !== '$') {
		throw new Error(`Expected JSON path to start with '$', got: ${path}`);
	}
	return [segments[0], ...segments.slice(1)];
}

// Use this sorter everywhere to be consistent, especially when you need to rely on comparisons.
// Right now, we don't export it because we rely on jsonSortedAndPrintedForSemanticDiffs() instead.
// But if we ever need to rely on just the sort (as opposed to sorting + printing), then consider
// exporting this function. Keep in mind that ideally, we want to keep the json sorting and printing
// logic within this pipeline only, instead of polluting the rest of the codebase with the use of
// internal functions like jsonSortedForSemanticDiffs and jsonPrintedForSemanticDiffs. So only export
// this if you really need to and then update this comment.
function jsonSortedForSemanticDiffs<T>(json: T): T {
	return deepSort(json);
}

// Use this pretty printer everywhere to be consistent, especially when you need to rely on comparisons.
// Regarding why this isn't exported, see comment for jsonSortedForSemanticDiffs - the same applies here.
//
// This custom printer is necessary because there are requirements that we need to adhere to such that
// the monaco diff editor prints empty arrays using two lines like:
// [
// ]
//
// instead of simply:
// []
//
// This is because the monaco diff editor needs to be able to denote an insertion to the array by
// inserting a "dummy line" between the "[" line and the "]" line. Same goes for objects.
function jsonPrintedForSemanticDiffs(value: JsonValue): string {
	return printJsonValue(value, 0) + "\n";
}

// Use this json sorter + printer everywhere in the codebase where you need to display json in a monaco editor. This not only
// makes it visually consistent but also gives the monaco diff editor consistent, predictable line numbers between the left
// and right sides. If the line numbers between the left and right sides aren't consistent or predictable, monaco will throw
// errors about "overlapping ranges" when you try to render diffs.
export function jsonSortedAndPrintedForSemanticDiffs(json: JsonValue): string {
	return jsonPrintedForSemanticDiffs(jsonSortedForSemanticDiffs(json));
}

type ParsedSemanticDiffCacheKey = {
	sortedOriginalJson: JsonObject | JsonArray,
	sortedModifiedJson: JsonObject | JsonArray
};
type SemanticDiffCacheKey = string;

function stringifiedSemanticDiffCacheKey(cacheKey: ParsedSemanticDiffCacheKey): SemanticDiffCacheKey {
	return JSON.stringify(cacheKey);
}
const semanticDiffCache = new Map<SemanticDiffCacheKey, SemanticDiffResults>();

/* -------------------------------------------------------------------------- */
/*                         MAIN GENERATION PIPELINE                           */
/* -------------------------------------------------------------------------- */

export async function generateSemanticDiff(
	originalJson: JsonObject | JsonArray,
	modifiedJson: JsonObject | JsonArray
): Promise<SemanticDiffResults> {
	// 1. Deep-sort JSON for deterministic structure
	// IMPORTANT! The comparator used by this sort needs to match the one used in computeRealOrVirtualRangeOfPath.
	const sortedOriginalJson = jsonSortedForSemanticDiffs(originalJson);
	const sortedModifiedJson = jsonSortedForSemanticDiffs(modifiedJson);

	const cacheKey = stringifiedSemanticDiffCacheKey({sortedOriginalJson, sortedModifiedJson});
	const cacheResult = semanticDiffCache.get(cacheKey);
	if (cacheResult) {
		// console.log("cache hit for ", originalJson, modifiedJson);
		return cacheResult;
	}
	// console.log("cache miss for ", originalJson, modifiedJson);

	// 2. Pretty-print both
	const sortedOriginalJsonPrinted = jsonPrintedForSemanticDiffs(sortedOriginalJson);
	const sortedModifiedJsonPrinted = jsonPrintedForSemanticDiffs(sortedModifiedJson);

	// 3. Build path to printed range maps
	const pathToPrintedRangeMapOriginal = buildPathToPrintedRangeMap(sortedOriginalJsonPrinted);
	const pathToPrintedRangeMapModified = buildPathToPrintedRangeMap(sortedModifiedJsonPrinted);

	// 4. Compute semantic deltas
	const semanticDeltas = diff(originalJson, modifiedJson);
	// console.log(semanticDeltas);

	const pathToSemanticDeltaMap = buildPathToSemanticDeltaMap(semanticDeltas);
	// console.log(pathToSemanticDeltaMap);

	// 5. Resolve delta paths → line numbers
	const deltaAndRangesByPath: PathToSemanticDeltaAndPrintedRangeMap = new Map();
	const semanticDeltaPaths = Array.from(pathToSemanticDeltaMap.keys());

	for (const semanticDeltaPath of semanticDeltaPaths) {
		const semanticDelta = pathToSemanticDeltaMap.get(semanticDeltaPath);
		if (semanticDelta === undefined) {
			throw new Error(`Could not find semantic delta for path ${semanticDeltaPath}.`);
		}

		// Remember that semanticDelta represents a delta for the path, meaning the path may exist only in original or only in modified. Or it may exist in both.
		// Let's say it exists in modified but not original (an addition). In that case, we need to compute where the path *would* have appeared in original. That's what pathRangeInOriginal would represent.
		// And vice versa for pathRangeInModified.
		const pathRangeInOriginal = computeRealOrVirtualRangeOfPath(semanticDeltaPath, pathToPrintedRangeMapOriginal, originalJson);
		const pathRangeInModified = computeRealOrVirtualRangeOfPath(semanticDeltaPath, pathToPrintedRangeMapModified, modifiedJson);

		deltaAndRangesByPath.set(semanticDeltaPath, {
			semanticDelta: semanticDelta,
			originalRange: pathRangeInOriginal,
			modifiedRange: pathRangeInModified,
		});
	}

	const result = { sortedOriginalJsonPrinted, sortedModifiedJsonPrinted, deltaAndRangesByPath };

	semanticDiffCache.set(cacheKey, result);

	return result;
}

/* -------------------------------------------------------------------------- */
/*                STEP 2: DETERMINISTIC JSON PRETTY PRINTER                   */
/* -------------------------------------------------------------------------- */

function printJsonValue(value: JsonValue, level: number): string {
	const indent = "  ".repeat(level);

	if (value === null) return `${indent}null`;
	if (typeof value === "number" || typeof value === "boolean")
		return `${indent}${value}`;
	if (typeof value === "string") return `${indent}${JSON.stringify(value)}`;

	if (Array.isArray(value)) return printArray(value, level);

	return printObject(value as Record<string, JsonValue>, level);
}

function printObject(obj: Record<string, JsonValue>, level: number): string {
	const indent = "  ".repeat(level);
	const childIndent = "  ".repeat(level + 1);
	const keys = Object.keys(obj);

	if (keys.length === 0) return `${indent}{\n${indent}}`;

	const lines: string[] = [];
	lines.push(`${indent}{`);

	keys.forEach((key, index) => {
		const value = obj[key]!;
		const keyStr = JSON.stringify(key);
		const isLast = index === keys.length - 1;

		if (
			value === null ||
			typeof value === "number" ||
			typeof value === "boolean" ||
			typeof value === "string"
		) {
			const scalar =
				typeof value === "string" ? JSON.stringify(value) : String(value);
			const comma = isLast ? "" : ",";
			lines.push(`${childIndent}${keyStr}: ${scalar}${comma}`);
		} else {
			const nested = printJsonValue(value, level + 1).split("\n");

			const first = nested[0];
			if (first === undefined) {
				throw new Error("Unexpected empty nested JSON output.");
			}
			const rest = nested.slice(1);

			const comma = isLast ? "" : ",";

			if (nested.length === 1) {
				lines.push(`${childIndent}${keyStr}: ${first.trim()}${comma}`);
			} else {
				lines.push(`${childIndent}${keyStr}: ${first.trimStart()}`);

				for (const line of rest.slice(0, -1)) {
					lines.push(line);
				}

				const lastRest = rest[rest.length - 1];
				lines.push(`${lastRest}${comma}`);
			}
		}
	});

	lines.push(`${indent}}`);
	return lines.join("\n");
}


function printArray(arr: JsonValue[], level: number): string {
	const indent = "  ".repeat(level);
	const childIndent = "  ".repeat(level + 1);

	if (arr.length === 0) return `${indent}[\n${indent}]`;

	const lines: string[] = [];
	lines.push(`${indent}[`);

	arr.forEach((value, index) => {
		const isLast = index === arr.length - 1;

		if (
			value === null ||
			typeof value === "number" ||
			typeof value === "boolean" ||
			typeof value === "string"
		) {
			const scalar =
				typeof value === "string" ? JSON.stringify(value) : String(value);
			const comma = isLast ? "" : ",";
			lines.push(`${childIndent}${scalar}${comma}`);
		} else {
			const nested = printJsonValue(value, level + 1).split("\n");
			const comma = isLast ? "" : ",";
			for (const line of nested.slice(0, -1)) lines.push(line);
			lines.push(`${nested[nested.length - 1]}${comma}`);
		}
	});

	lines.push(`${indent}]`);
	return lines.join("\n");
}

/* -------------------------------------------------------------------------- */
/*       STEP 3: BUILD PATH TO RANGE MAP (json-to-ast with loc: true)         */
/* -------------------------------------------------------------------------- */

function buildPathToPrintedRangeMap(text: string): PathToPrintedRangeMapContainer {
	const ast = parseJsonAst(text, { loc: true });
	const rangesByPath = new Map<CanonicalPathString, PrintedRange>();

	function visit(node: ASTNode, pathSegments: CanonicalPathSegments): void {
		const path = jsonPathArrayToString(pathSegments);

		if (node.loc) {
			rangesByPath.set(path, {
				startLine: node.loc.start.line,
				endLine: node.loc.end.line,
			});
		}

		if (node.type === "Object") visitObjectItems(node as ObjectNode, pathSegments);
		else if (node.type === "Array") visitArrayItems(node as ArrayNode, pathSegments);
	}

	function visitObjectItems(obj: ObjectNode, pathSegments: CanonicalPathSegments): void {
		for (const prop of obj.children) {
			const childKey = prop.key.value;

			const childPath: CanonicalPathSegments = [...pathSegments, childKey];

			if (prop.key.loc && prop.value.loc) {
				rangesByPath.set(jsonPathArrayToString(childPath), {
					startLine: prop.key.loc.start.line,
					endLine: prop.value.loc.end.line,
				});
			}

			visit(prop.value as ASTNode, childPath);
		}
	}

	function visitArrayItems(arr: ArrayNode, pathSegments: CanonicalPathSegments): void {
		(arr.children).forEach((el, idx) => {
			if (!el.loc) return;

			const childPath: CanonicalPathSegments = [...pathSegments, `${idx}`];

			rangesByPath.set(jsonPathArrayToString(childPath), {
				startLine: el.loc.start.line,
				endLine: el.loc.end.line,
			});

			visit(el, childPath);
		});
	}

	visit(ast, ['$']);

	return {
		rangesByPath,
		rootStart: ast.loc!.start.line,
		rootEnd: ast.loc!.end.line,
	};
}

/* -------------------------------------------------------------------------- */
/*                     STEP 4: CANONICAL PATH → DELTA MAP                     */
/* -------------------------------------------------------------------------- */

function buildPathToSemanticDeltaMap(deltas: Difference[]): PathToSemanticDeltaMap {
	const map: PathToSemanticDeltaMap = new Map();

	for (const delta of deltas) {
		const canonicalPathSegments: CanonicalPathSegments = ['$', ...(delta.path.map((val) => val.toString()))];
		map.set(jsonPathArrayToString(canonicalPathSegments), delta);
	}
	
	return map;
}

/* -------------------------------------------------------------------------- */
/*           STEP 5: COMPUTE LINE NUMBERS (INCLUDING VIRTUAL INSERTION)       */
/* -------------------------------------------------------------------------- */

function computeRealOrVirtualRangeOfPath(
	// This path may or may not exist in entireJsonForTargetSide:
	// 	- If it does exist, we'll get a real range: where it exists in the sorted and printed version of entireJsonForTargetSide.
	//	- If it does not exist, we'll get a virtual range: where it would have existed in the sorted and printed version of entireJsonForTargetSide.
	// When computing the virtual range in this function, it's CRITICAL to respect the expected sort order (the same one used at the top of the pipeline to deep sort). Otherwise, it will screw up the entire diff where you'll end up with a criss cross of virtual ranges that are impossible to show and Monaco will throw an error.
	pathFromEitherSide: CanonicalPathString,
	// All vars below of the form `*forTargetSide` are expected to belong to the same side
	rangesByPathForTargetSideContainer: PathToPrintedRangeMapContainer,
	// This does not need to be passed in as a pre-sorted json object. However, it is still crucial that we sort it before we determine
	// any sort of printed range, and that's why we will be sorting this below before we determine a virtual range.
	entireJsonForTargetSide: JsonObject | JsonArray
): PrintedRange {

	const rangesByPathForTargetSide = rangesByPathForTargetSideContainer.rangesByPath;
	// console.log('path ', pathFromEitherSide);

	// Return a real range since there's a matching path. This could be because either:
	//  1. Both pathFromEitherSide and rangesByPathForTargetSide belong to the same side, so ofcourse there's a matching path, or,
	//  2. Even though pathFromEitherSide and rangesByPathForTargetSide belong to different sides, there's a matching path on the target side.
	const existingPathRange = rangesByPathForTargetSide.get(pathFromEitherSide);
	if (existingPathRange) {
		return existingPathRange;
	}
	const fallback = { startLine: rangesByPathForTargetSideContainer.rootEnd, endLine: rangesByPathForTargetSideContainer.rootEnd };
	// At this point, the path does not exist in the provided map. So, need to compute a virtual range: where it *would* have appeared if it did exist.
	const pathSegments = jsonPathStringToArray(pathFromEitherSide);
	const currSegmentKey = pathSegments[pathSegments.length - 1];
	if (currSegmentKey === undefined) {
		return fallback;
	}
	const parentPathSegments = jsonPathArrayToParent(pathSegments);
	if (parentPathSegments === undefined) {
		// No parent path means this is the root path "$", which must exist.
		// But just in case, return the root range.
		return fallback;
	}
	const parentPath: CanonicalPathString = jsonPathArrayToString(parentPathSegments);
	const parentPathRange = rangesByPathForTargetSide.get(parentPath);
	if (!parentPathRange) {
		// Parent path does not exist, so we cannot determine where this path would go.
		return fallback;
	}

	// Crucial to sort the json so that we can accurately determine the virtual range.
	const sortedEntireJsonForTargetSide = jsonSortedForSemanticDiffs(entireJsonForTargetSide);
	
	const parentArrayOrObject = JSONPath({
		path: parentPath,
		json: sortedEntireJsonForTargetSide,
		resultType: 'value'
	})[0];

	if (parentArrayOrObject === null || (!Array.isArray(parentArrayOrObject) && typeof parentArrayOrObject !== 'object')) {
		// This should never actually happen unless we passed in a pathFromEitherSide that was too deeply nested for even it's parent to be present in entireJsonForTargetSide. This would imply that we were generating semantic deltas where the path was erroneously too deep.
		console.log(JSONPath({
			path: parentPath,
			json: sortedEntireJsonForTargetSide,
			resultType: 'value'
		}))
		throw new Error(`Fetching the parent of ${pathFromEitherSide} led to neither an array nor an object. Take a look at the semantic diff generator - it's most likely giving us an invalid delta by going too deep. Tried looking through object ${JSON.stringify(sortedEntireJsonForTargetSide)}.`);
	}
	
	if (Array.isArray(parentArrayOrObject)) {
		const maxIndexOfSiblings = parentArrayOrObject.length - 1;
		const indexOfCurrSegment = parseInt(currSegmentKey);

		if (indexOfCurrSegment > maxIndexOfSiblings) {
			const siblingPath: CanonicalPathString = jsonPathArrayToString([...parentPathSegments, maxIndexOfSiblings.toString()]);
			const rangeOfMaxSibling = rangesByPathForTargetSide.get(siblingPath);
			if (rangeOfMaxSibling === undefined) {
				throw new Error(`Could not find path ${siblingPath}`);
			}
			return { startLine: rangeOfMaxSibling.endLine + 1, endLine: rangeOfMaxSibling.endLine + 1 };
		}
		else {
			const siblingPath: CanonicalPathString = jsonPathArrayToString([...parentPathSegments, indexOfCurrSegment.toString()]);
			// If we had an exact match, throw an error because a valid existingPathRange at the top of this function would have returned it already. This means there's a bug in buildPathToPrintedRangeMap().
			throw new Error(`buildPathToPrintedRangeMap() gave us an incomplete or invalid path->printedRange map. Fix that function because it was supposed to give us a proper range for ${siblingPath}`);
			// const rangeOfExistingSibling = rangesByPathForTargetSide.get(siblingPath);
			// if (rangeOfExistingSibling === undefined) {
			// 	throw new Error(`Could not find path ${siblingPath}`);
			// }
			// console.log("SHOULD NEVER HAPPEN key index ", currSegmentKey, rangeOfExistingSibling);
			// return { startLine: rangeOfExistingSibling.startLine, endLine: rangeOfExistingSibling.endLine };
		}

	}
	else {
		const sortedSiblingKeys = Object.keys(parentArrayOrObject);
		// Alphabetical insertion before next sibling
		// Remember that siblingKeys are sorted in ascending order. And so, as we iterate through them, siblingKey will become larger and larger.
		// So, the moment we find a siblingKey that is larger than currSegmentKey, we know that currSegmentKey should be inserted before that siblingKey.
		// console.log(sortedSiblingKeys);
		for (const siblingKey of sortedSiblingKeys) {			
			const siblingPath = jsonPathArrayToString([...parentPathSegments, siblingKey]);

			if (siblingKey === currSegmentKey) {
				// If we had an exact match, throw an error because a valid existingPathRange at the top of this function would have returned it already. This means there's a bug in buildPathToPrintedRangeMap().
				throw new Error(`buildPathToPrintedRangeMap() gave us an incomplete or invalid path->printedRange map. Fix that function because it was supposed to give us a proper range for ${siblingPath}`);
				// const siblingPathRange = rangesByPathForTargetSide.get(siblingPath);
				// if (siblingPathRange) {
				// 	console.log("SHOULD NEVER HAPPEN object key", currSegmentKey, siblingKey, siblingPathRange);
				// 	return { startLine: siblingPathRange.startLine, endLine: siblingPathRange.endLine };
				// }
			}
			else {
				const comparisonObj = {
					[siblingKey]: true,
					[currSegmentKey]: true,
				};
				// This needs to be the same sort as the one in the pretty-printer
				const tempSortedKeys = Object.keys(jsonSortedForSemanticDiffs(comparisonObj));

				const isSiblingKeyAfterCurr = tempSortedKeys.indexOf(currSegmentKey) < tempSortedKeys.indexOf(siblingKey);

				if (isSiblingKeyAfterCurr) {
					const siblingPathRange = rangesByPathForTargetSide.get(jsonPathArrayToString([...parentPathSegments, siblingKey]));
					if (siblingPathRange) {
						return { startLine: siblingPathRange.startLine, endLine: siblingPathRange.startLine };
					}
				}
			}
		}
		// Fallback → after last sibling, before parent close. This is what gets used when there are no siblings, too.
		return { startLine: parentPathRange.endLine, endLine: parentPathRange.endLine };
	}
}

/* -------------------------------------------------------------------------- */
/*        STEP 5: semanticDiff → IDocumentDiff adapter for use in Monaco      */
/* -------------------------------------------------------------------------- */

function stringDiffMatchPatchesToRangeMappings(
	stringDiffs: diff_match_patch.Diff[],
	originalStartLine: number,
	originalEndLine: number,
	modifiedStartLine: number,
	modifiedEndLine: number,
): RangeMapping[] {

	const rangeMappings: RangeMapping[] = [];

	let indexInOriginal = 1; // 1-based index is what monaco Range expects
	let indexInModified = 1;
	for (const diff of stringDiffs) {
		const isIdentical	=	diff[0] === 0;
		const isRemoved		= diff[0] === -1;
		const isAdded			= diff[0] === 1;
		const diffLength	= diff[1].length

		if (isIdentical) {
			indexInOriginal += diffLength;
			indexInModified += diffLength;
		}
		else {
			if (isRemoved) {
				indexInOriginal += diffLength;
				rangeMappings.push(new RangeMapping(
					new Range(originalStartLine, indexInOriginal - diffLength, originalEndLine, indexInOriginal) as any,
					new Range(modifiedStartLine, indexInModified, modifiedEndLine, indexInModified) as any,
				));
			}
			else { // isAdded
				indexInModified += diffLength;
				rangeMappings.push(new RangeMapping(
					new Range(originalStartLine, indexInOriginal, originalEndLine, indexInOriginal) as any,
					new Range(modifiedStartLine, indexInModified - diffLength, modifiedEndLine, indexInModified) as any,
				));
			}
		}
	}

	return rangeMappings;
}

export function semanticDiffToDocumentDiff(
	results: SemanticDiffResults
): IDocumentDiff {
	const changes: DetailedLineRangeMapping[] = [];
	
	for (const [canonicalPath, semDeltaAndRange] of results.deltaAndRangesByPath) {
		const originalLoc = semDeltaAndRange.originalRange;
		const modifiedLoc = semDeltaAndRange.modifiedRange;
		const originalLines = results.sortedOriginalJsonPrinted.split("\n");
		const modifiedLines = results.sortedModifiedJsonPrinted.split("\n");

		let origEndLineOffset = 0;
		let modEndLineOffset = 0;
		let rangeMappings: RangeMapping[] = [];

		switch (semDeltaAndRange.semanticDelta.type) {
			case "CHANGE":
				// Need to add 1 line to indicate an actual line being changed
				origEndLineOffset = 1;
				modEndLineOffset = 1;

				const oldVal = semDeltaAndRange.semanticDelta.oldValue;
				const newVal = semDeltaAndRange.semanticDelta.value;
				
				const isOldValPrimitive = oldVal === null || typeof oldVal !== 'object';
				const isNewValPrimitive = newVal === null || typeof newVal !== 'object';					

				if (isOldValPrimitive && isNewValPrimitive) {
					const originalLineText = originalLines[originalLoc.startLine - 1];
					const modifiedLineText = modifiedLines[modifiedLoc.startLine - 1];

					if (originalLineText === undefined || modifiedLineText === undefined) {
						throw new Error(`Could not fetch lines by path: ${semDeltaAndRange.semanticDelta.path}`);
					}

					const valueDiffer = new DiffMatchPatch();
					const valueDiffs = valueDiffer.diff_main(originalLineText, modifiedLineText);
					valueDiffer.diff_cleanupSemantic(valueDiffs); // This mutates stringDiffs.

					rangeMappings = stringDiffMatchPatchesToRangeMappings(valueDiffs, originalLoc.startLine, originalLoc.endLine, modifiedLoc.startLine, modifiedLoc.endLine);
				}
				break;

			case "REMOVE":
				// Full block removed on the left; anchor-only on the right
				origEndLineOffset = 1;
				modEndLineOffset = 0; // Indicate anchor-only
				break;

			case "CREATE":
				// Anchor-only on the left; full block on the right
				origEndLineOffset = 0; // Indicate anchor-only
				modEndLineOffset = 1;
				break;
		}
		
		// console.log(`Modified (${semDeltaAndRange.semanticDelta.type}): ${canonicalPath} → ${originalLoc.startLine}-${originalLoc.endLine + origEndLineOffset} to ${modifiedLoc.startLine}-${modifiedLoc.endLine + modEndLineOffset}`);
		const origRange = new LineRange(originalLoc.startLine, originalLoc.endLine + origEndLineOffset);
		const modRange = new LineRange(modifiedLoc.startLine, modifiedLoc.endLine + modEndLineOffset);

		changes.push(
			new DetailedLineRangeMapping(
				origRange as any,
				modRange as any,
				rangeMappings
			)
		);
	}

	// Super important sorting needed so that monaco doesn't throw an error about out-of-order line mappings.
	changes.sort((a,b) => {
		// 1. Sort primarily by original start
		const o = a.original.startLineNumber - b.original.startLineNumber;
		if (o !== 0) return o;

		// 2. If equal, sort by original end
		return a.original.endLineNumberExclusive - b.original.endLineNumberExclusive;
		// if (oe !== 0) return oe;

		// // 3. Secondary: ensure modified ordering is monotonic
		// const m = a.modified.startLineNumber - b.modified.startLineNumber;
		// if (m !== 0) return m;

		// // 4. Final tie-breaker by modified end
		// return a.modified.endLineNumberExclusive - b.modified.endLineNumberExclusive;
	});


	return {
		identical: changes.length === 0,
		quitEarly: false,
		changes,
		moves: [],
	};
}
